// Store checks: what isn't selling, next month's legal prices, over and short by cashier, and what makes the money.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { addDays, todayIn } from '@njpos/core';
import { api, dayLabel, monthLabel, sizeLabel, usd, usd0 } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';
import DateRange, { downloadCsv, type Range } from '../components/DateRange';

type Tab = 'dead' | 'prices' | 'drawers' | 'profit';
const TABS: [Tab, string][] = [['dead', 'Dead stock'], ['prices', 'Next month’s prices'], ['drawers', 'Over and short'], ['profit', 'What makes money']];
const last30 = (): Range => { const t = todayIn(); return { from: addDays(t, -29), to: t }; };

export default function StoreChecks() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find(([k]) => k === params.get('tab'))?.[0] ?? 'dead') as Tab;
  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Store checks</h1><p>The weekly questions: what is not selling, which prices next month’s CPL makes illegal, whose drawer is off, and what really makes the money.</p></div></div>
      <div className="row wrap" role="tablist">{TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'primary' : ''} onClick={() => setParams({ tab: k })}>{l}</button>)}</div>
      {tab === 'dead' && <DeadStock />}
      {tab === 'prices' && <Prices />}
      {tab === 'drawers' && <Drawers />}
      {tab === 'profit' && <Profit />}
    </div>
  );
}

// ---------------- dead stock ----------------
function DeadStock() {
  const [days, setDays] = useState(60);
  const r = useLoad(() => api<any>(`/api/checks/dead-stock?days=${days}`), [days]);
  const d = r.data;
  return (
    <>
      <div className="row wrap">
        {[30, 60, 90, 180].map((n) => <button key={n} className={days === n ? 'primary' : ''} onClick={() => setDays(n)}>No sale in {n} days</button>)}
        <span className="grow" />
        {d?.items.length > 0 && <button onClick={() => downloadCsv(`dead-stock-${days}-days.csv`, [['Brand', 'Name', 'Size', 'On hand', 'Last sold', 'Days since sale', 'Cost each', 'Cash tied up', 'Retail value', 'Distributor', 'Location'],
          ...d.items.map((i: any) => [i.brand, i.name, sizeLabel(i.size_ml), i.on_hand, i.last_sold ?? 'never', i.days_since_sale ?? '', (i.unit_cost_cents ?? 0) / 100, i.cost_value_cents / 100, i.retail_value_cents / 100, i.wholesaler ?? '', i.location ?? ''])])}>Download CSV</button>}
      </div>
      <ErrorNote error={r.error} />
      {d && (
        <div className="stats">
          <div><div className="v">{usd0(d.totals.cost_value_cents)}</div><div className="l">Cash tied up, at cost</div></div>
          <div><div className="v">{d.totals.items}</div><div className="l">Items, {d.totals.units} bottles</div></div>
          <div><div className="v">{d.totals.share_of_stock_pct}%</div><div className="l">Of all stock value</div></div>
          <div><div className="v">{usd0(d.totals.retail_value_cents)}</div><div className="l">At shelf price</div></div>
        </div>
      )}
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Product</th><th className="n">On hand</th><th>Last sold</th><th className="n">Cost each</th><th className="n">Cash tied up</th><th className="n">Shelf</th><th>Distributor</th></tr></thead>
          <tbody>{d?.items.map((i: any) => (
            <tr key={i.item_id}>
              <td><div style={{ fontWeight: 600 }}>{i.brand} {i.name}</div><div className="faint">{sizeLabel(i.size_ml)}{i.location ? ` · ${i.location}` : ''}</div></td>
              <td className="n">{i.on_hand}</td>
              <td>{i.last_sold ? <>{i.last_sold} <span className="faint">({i.days_since_sale} days)</span></> : <span className="chip bad">Never</span>}</td>
              <td className="n">{i.has_cost ? usd(i.unit_cost_cents) : <span className="chip warn">No cost</span>}</td>
              <td className="n" style={{ fontWeight: 650 }}>{usd(i.cost_value_cents)}</td>
              <td className="n">{usd(i.retail_cents)}</td>
              <td>{i.wholesaler ?? <span className="faint">—</span>}</td>
            </tr>))}</tbody>
        </table>
        {d?.items.length === 0 && <div className="empty">Everything with stock has sold in the last {days} days.</div>}
      </div></div>
      {d && <p className="faint">Cash tied up is on hand × the last invoice cost. {d.totals.too_new_to_judge > 0 && `${d.totals.too_new_to_judge} item(s) first stocked in the last ${days} days are left out as too new to judge. `}
        To move these, mark them down in <Link to="/promotions">Promotions</Link>, ask the distributor about a return or swap, or stop reordering them.</p>}
    </>
  );
}

// ---------------- next month's prices ----------------
const STATUS: Record<string, [string, string, string]> = {
  illegal: ['Illegal', 'bad', 'Under even the best deal’s cost. NJ does not allow it; the register will refuse it.'],
  below_frontline: ['Below cost', 'warn', 'Under the frontline cost. Legal only if you bought it on a deal.'],
  cost_up: ['Cost going up', 'amber', 'Still legal, but the margin shrinks.'],
  cost_down: ['Cost going down', 'good', 'Room to lower the price or keep the margin.'],
};

function Prices() {
  const r = useLoad(() => api<any>('/api/checks/prices'), []);
  const d = r.data;
  return (
    <>
      <ErrorNote error={r.error} />
      {d && (
        <div className="stats">
          <div><div className="v" style={{ color: d.counts.illegal ? 'var(--bad)' : undefined }}>{d.counts.illegal}</div><div className="l">Illegal on {dayLabel(d.starts)}</div></div>
          <div><div className="v">{d.counts.below_frontline}</div><div className="l">Below frontline cost</div></div>
          <div><div className="v">{d.counts.cost_up}</div><div className="l">Cost going up</div></div>
          <div><div className="v">{d.days_until}</div><div className="l">Days until the new CPL</div></div>
        </div>
      )}
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Product</th><th>Status</th><th className="n">Shelf now</th><th className="n">Cost now</th><th className="n">Next frontline</th><th className="n">Lowest legal</th><th className="n">Raise to</th></tr></thead>
          <tbody>{d?.items.map((i: any) => {
            const [label, tone, why] = STATUS[i.status]!;
            return (
              <tr key={i.item_id} title={why}>
                <td><div style={{ fontWeight: 600 }}>{i.brand} {i.name}</div><div className="faint">{sizeLabel(i.size_ml)}</div></td>
                <td><span className={`chip ${tone}`}>{label}</span></td>
                <td className="n">{usd(i.retail_cents)}</td>
                <td className="n">{usd(i.last_unit_cost_cents)}</td>
                <td className="n">{usd(i.next_frontline_unit_cents)}{i.cost_change_cents ? <div className={`faint`}>{i.cost_change_cents > 0 ? '+' : ''}{usd(i.cost_change_cents)}</div> : null}</td>
                <td className="n">{usd(i.next_min_legal_unit_cents)}</td>
                <td className="n" style={{ fontWeight: 650 }}>{i.raise_to_at_least_cents ? usd(i.raise_to_at_least_cents) : '—'}</td>
              </tr>);
          })}</tbody>
        </table>
        {d?.items.length === 0 && <div className="empty">No price changes for {d && monthLabel(d.starts)} yet. They show up once next month’s CPL is loaded (around the 18th).</div>}
      </div></div>
      <p className="faint">Lowest legal = the best CPL case price for next month ÷ bottles per case. Change prices on <Link to="/items">Items</Link>, or all at once from <Link to="/buy">Buy list</Link> → price worklist.</p>
    </>
  );
}

// ---------------- over and short ----------------
function Drawers() {
  const [range, setRange] = useState<Range>(last30());
  const r = useLoad(() => api<any>(`/api/checks/drawers?from=${range.from}&to=${range.to}`), [range.from, range.to]);
  const d = r.data;
  return (
    <>
      <DateRange value={range} onChange={setRange} />
      <ErrorNote error={r.error} />
      {d && (
        <div className="stats">
          <div><div className="v" style={{ color: d.totals.short_cents ? 'var(--bad)' : undefined }}>{usd(d.totals.short_cents)}</div><div className="l">Short, all drawers</div></div>
          <div><div className="v">{usd(d.totals.over_cents)}</div><div className="l">Over, all drawers</div></div>
          <div><div className="v">{d.totals.drawers}</div><div className="l">Drawers closed</div></div>
          <div><div className="v">{d.totals.watch}</div><div className="l">Cashiers to talk to</div></div>
        </div>
      )}
      <div className="panel"><div className="panel-head"><h2>By cashier</h2></div><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Cashier</th><th className="n">Drawers</th><th className="n">Balanced</th><th className="n">Times short</th><th className="n">Total short</th><th className="n">Worst</th><th className="n">Times over</th><th className="n">Total over</th><th /></tr></thead>
          <tbody>{d?.cashiers.map((c: any) => (
            <tr key={c.user_id}>
              <td style={{ fontWeight: 600 }}>{c.name}</td><td className="n">{c.drawers}</td><td className="n">{c.balanced}</td>
              <td className="n">{c.short_count}</td><td className="n">{usd(c.short_cents)}</td><td className="n">{c.worst_short_cents ? usd(c.worst_short_cents) : '—'}</td>
              <td className="n">{c.over_count}</td><td className="n">{usd(c.over_cents)}</td>
              <td>{c.watch && <span className="chip bad">Watch</span>}</td>
            </tr>))}</tbody>
        </table>
        {d?.cashiers.length === 0 && <div className="empty">No drawers closed in these dates.</div>}
      </div></div>
      {d?.drawers.length > 0 && (
        <div className="panel"><div className="panel-head"><h2>Drawers that were off</h2></div><div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Date</th><th>Cashier</th><th className="n">Should have</th><th className="n">Counted</th><th className="n">Off by</th><th>Note</th></tr></thead>
            <tbody>{d.drawers.map((x: any) => (
              <tr key={x.id}><td>{x.date}</td><td>{x.cashier}</td><td className="n">{usd(x.expected_cents)}</td><td className="n">{usd(x.counted_cents)}</td>
                <td className="n"><span className={`chip ${x.variance_cents < 0 ? 'bad' : 'warn'}`}>{x.variance_cents > 0 ? '+' : ''}{usd(x.variance_cents)}</span></td><td className="faint">{x.note ?? ''}</td></tr>))}</tbody>
          </table>
        </div></div>
      )}
      <p className="faint">Overs and shorts are added up separately, so a $20 over one day cannot hide a $20 short the next. “Watch” means short on 3 or more drawers, or $20 or more in total. Refunds and voids are on <Link to="/analytics">Analytics</Link> and the <Link to="/fraud">AI Fraud Detector</Link>.</p>
    </>
  );
}

// ---------------- what makes money ----------------
function Profit() {
  const [range, setRange] = useState<Range>(last30());
  const [group, setGroup] = useState<'item' | 'brand' | 'category'>('item');
  const [sort, setSort] = useState<'profit' | 'sales'>('profit');
  const r = useLoad(() => api<any>(`/api/checks/profit?from=${range.from}&to=${range.to}&group=${group}&sort=${sort}&limit=200`), [range.from, range.to, group, sort]);
  const d = r.data;
  return (
    <>
      <DateRange value={range} onChange={setRange} />
      <div className="row wrap">
        <label>By<select value={group} onChange={(e) => setGroup(e.target.value as any)}><option value="item">Item</option><option value="brand">Brand</option><option value="category">Category</option></select></label>
        <label>Sort by<select value={sort} onChange={(e) => setSort(e.target.value as any)}><option value="profit">Profit</option><option value="sales">Sales</option></select></label>
      </div>
      <ErrorNote error={r.error} />
      {d && (
        <div className="stats">
          <div><div className="v">{usd0(d.totals.profit_cents)}</div><div className="l">Gross profit</div></div>
          <div><div className="v">{usd0(d.totals.net_cents)}</div><div className="l">Net sales</div></div>
          <div><div className="v">{d.totals.margin_pct}%</div><div className="l">Margin</div></div>
          <div><div className="v">{d.totals.groups}</div><div className="l">{group === 'item' ? 'Items' : group === 'brand' ? 'Brands' : 'Categories'} sold</div></div>
        </div>
      )}
      {d?.thin_margin.length > 0 && (
        <div className="notice"><b>Big sellers, thin margin:</b> {d.thin_margin.map((t: any) => `${t.label} (${t.margin_pct}%)`).join(', ')}. These move a lot of dollars but keep little of each. Check their CPL deals and shelf prices.</div>
      )}
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>{group === 'item' ? 'Item' : group === 'brand' ? 'Brand' : 'Category'}</th><th className="n">Units</th><th className="n">Net sales</th><th className="n">Cost</th><th className="n">Profit</th><th className="n">Margin</th><th className="n">Share of profit</th></tr></thead>
          <tbody>{d?.rows.map((x: any) => (
            <tr key={x.key}><td style={{ fontWeight: 600 }}>{group === 'category' ? String(x.label).replace('_', ' ') : x.label}</td><td className="n">{x.units}</td><td className="n">{usd(x.net_cents)}</td><td className="n">{usd(x.cogs_cents)}</td>
              <td className="n" style={{ fontWeight: 650 }}>{usd(x.profit_cents)}</td><td className="n">{x.margin_pct}%</td><td className="n">{x.profit_share_pct}%</td></tr>))}</tbody>
        </table>
        {d?.rows.length === 0 && <div className="empty">No sales in these dates.</div>}
      </div></div>
      {d?.rows.length > 0 && <div><button onClick={() => downloadCsv(`profit-by-${group}-${range.from}-to-${range.to}.csv`, [['Name', 'Units', 'Net sales', 'Cost', 'Profit', 'Margin %', 'Share of profit %'], ...d.rows.map((x: any) => [x.label, x.units, x.net_cents / 100, x.cogs_cents / 100, x.profit_cents / 100, x.margin_pct, x.profit_share_pct])])}>Download CSV</button></div>}
    </>
  );
}
